/**
 * Chatbot adapter for SMSConfig: describes the page (fields + APIs) and knows
 * how to map a third-party SMS API request onto this page's data model.
 */
export const DEFAULT_QUERY_MAP = { phone: "phoneNumber", message: "message", senderId: "senderId", dltId: "templateID" };

const isGet = (v) => v.httpMethod === "GET";
const isPost = (v) => v.httpMethod === "POST";

export const SMS_FIELDS = [
  { key: "providerName", label: "Provider Name", type: "string", required: true, aliases: ["provider"] },
  { key: "httpMethod", label: "HTTP Method", type: "enum", options: ["GET", "POST"], required: true, aliases: ["method"] },
  { key: "url", label: "Base URL", type: "url", required: true },
  { key: "queryParamMapping", label: "Query Param Mapping", type: "keyvalue", when: isGet,
    description: "Our variable (phone, message, senderId, dltId) -> provider's parameter name" },
  { key: "staticQueryParams", label: "Static Query Params (API keys)", type: "keyvalue", sensitive: true, when: isGet },
  { key: "contentType", label: "Content Type", type: "string", when: isPost },
  { key: "customHeaders", label: "Custom Headers", type: "keyvalue", sensitive: true, when: isPost },
  { key: "payloadTemplate", label: "Payload Template", type: "json", when: isPost,
    description: "Must use {{phone}}, {{message}}, {{senderId}}, {{dltId}}" },
  { key: "responseSuccessField", label: "Success Field", type: "string", when: isPost },
  { key: "responseSuccessValue", label: "Success Value", type: "string", when: isPost },
];

export const SMS_APIS = [
  { method: "GET", path: "/api/smsConfig/company/{companyId}", purpose: "Load saved gateway" },
  { method: "POST", path: "/api/smsConfig", purpose: "Create gateway" },
  { method: "PUT", path: "/api/smsConfig/update/{companyId}", purpose: "Update gateway" },
  { method: "DELETE", path: "/api/smsConfig/delete/{companyId}", purpose: "Delete gateway" },
];

/* ---- spec -> SMS values ---- */
const ROLES = {
  phone: /^(to|mobile|mobileno|mobilenumber|phone|number|numbers|msisdn|recipient|receiver|dest|destination)$/i,
  message: /^(msg|message|text|body|sms|smstext|content)$/i,
  senderId: /^(sender|senderid|sender_id|from|sid|source|header)$/i,
  dltId: /^(dlt|dltid|dlt_te_id|dlt_template_id|templateid|template_id|tid|peid)$/i,
};
const roleOf = (key, val) => {
  const ph = String(val).match(/\{\{\s*(phone|message|senderId|dltId)\s*\}\}/);
  if (ph) return ph[1];
  return Object.keys(ROLES).find((r) => ROLES[r].test(key));
};

export const mapSmsSpec = (spec) => {
  const out = { httpMethod: spec.method === "GET" ? "GET" : "POST", url: spec.baseUrl };
  try {
    const host = new URL(spec.baseUrl).hostname.replace(/^(www|api)\./, "").split(".")[0];
    out.providerName = host.charAt(0).toUpperCase() + host.slice(1);
  } catch { /* ignore */ }

  if (out.httpMethod === "GET") {
    const mapping = {}, staticParams = {};
    Object.entries(spec.query).forEach(([k, v]) => {
      const role = roleOf(k, v);
      if (role && !mapping[role]) mapping[role] = k;
      else staticParams[k] = v;
    });
    out.queryParamMapping = mapping;
    out.staticQueryParams = staticParams;
  } else {
    const headers = { ...spec.headers };
    Object.keys(headers).forEach((k) => k.toLowerCase() === "content-type" && delete headers[k]);
    out.contentType = spec.contentType?.split(";")[0] || "application/json";
    out.customHeaders = headers;
    // keep the user's sample body; replace obvious sample values with our variables
    let body = spec.body || "";
    if (spec.bodyJson) {
      const swap = (o) =>
        Object.fromEntries(
          Object.entries(o).map(([k, v]) => {
            if (v && typeof v === "object") return [k, swap(v)];
            const role = roleOf(k, v);
            return [k, role ? `{{${role}}}` : v];
          })
        );
      body = JSON.stringify(swap(spec.bodyJson), null, 2);
    }
    out.payloadTemplate = body;
    // common success conventions; user reviews these in the card
    out.responseSuccessField = "status";
    out.responseSuccessValue = "success";
  }
  return out;
};

/* ---- page <-> chatbot data model (key/value lists <-> plain objects) ---- */
const toObject = (arr) =>
  (arr || []).reduce((a, r) => (r.key?.trim() ? { ...a, [r.key.trim()]: (r.value || "").trim() } : a), {});
export const toRows = (obj, fallback) => {
  const rows = Object.entries(obj || {}).map(([key, value]) => ({ key, value }));
  return rows.length ? rows : fallback;
};
export { toObject };
